import type { InventoryUnitRecord } from '@/renderer/types/inventory';
import { useInventoryUnits, useSaveInventoryUnit } from '@hooks/inventory';
import { LoadingState } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import React, { useMemo, useState } from 'react';
import { InventorySettingsTable } from './InventorySettingsTable';
import { InventoryUnitEditorModal } from './InventoryUnitEditorModal';
import {
  buildSaveInventoryUnitPayload,
  InventoryUnitFormState,
} from './inventorySettingsForm';

const EMPTY_UNITS: InventoryUnitRecord[] = [];

export function InventoryUnitsPageContent() {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const unitsQuery = useInventoryUnits(companyId);
  const saveUnitMutation = useSaveInventoryUnit();

  const [search, setSearch] = useState('');
  const [editingUnit, setEditingUnit] = useState<InventoryUnitRecord | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const units = unitsQuery.data ?? EMPTY_UNITS;

  const filteredUnits = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    if (!searchValue) {
      return units;
    }

    return units.filter((unit) =>
      [unit.name, unit.abbreviation ?? '']
        .join(' ')
        .toLowerCase()
        .includes(searchValue),
    );
  }, [units, search]);

  if (unitsQuery.isLoading) {
    return <LoadingState message="Loading inventory units..." />;
  }

  const handleSave = async (form: InventoryUnitFormState) => {
    if (!companyId) {
      return;
    }

    await saveUnitMutation.mutateAsync(
      buildSaveInventoryUnitPayload(companyId, form, editingUnit?.id),
    );
    setIsModalOpen(false);
    setEditingUnit(null);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-pos-primary">
              Inventory items
            </p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-950">
              Units
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Units define how items are purchased, stocked, and consumed so
              conversions stay consistent across the platform.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-pos-primary/10 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary">
                Total units
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-slate-950">
                {units.length}
              </p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Active units
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-slate-950">
                {units.filter((item) => item.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <InventorySettingsTable
        title="Inventory units"
        description="Units keep purchasing, stock balances, and recipe consumption aligned."
        searchLabel="Find unit"
        searchValue={search}
        createLabel="New unit"
        rows={filteredUnits.map((unit) => ({
          id: unit.id,
          title: unit.name,
          subtitle: unit.abbreviation
            ? `Abbreviation: ${unit.abbreviation}`
            : 'No abbreviation set',
          meta: `Updated ${new Date(unit.updated_at).toLocaleDateString()}`,
          status: unit.is_active,
        }))}
        emptyMessage="No units match this filter yet."
        onSearchChange={setSearch}
        onCreate={() => {
          setEditingUnit(null);
          setIsModalOpen(true);
        }}
        onEdit={(id) => {
          const unit = units.find((item) => item.id === id) ?? null;
          setEditingUnit(unit);
          setIsModalOpen(true);
        }}
        onToggleStatus={(id) => {
          const unit = units.find((item) => item.id === id);
          if (!unit || !companyId) {
            return;
          }

          saveUnitMutation.mutateAsync({
            id: unit.id,
            companyId,
            name: unit.name,
            abbreviation: unit.abbreviation,
            isActive: !unit.is_active,
          });
        }}
      />

      {isModalOpen ? (
        <InventoryUnitEditorModal
          unit={editingUnit}
          isSaving={saveUnitMutation.isPending}
          onClose={() => {
            setIsModalOpen(false);
            setEditingUnit(null);
          }}
          onSave={handleSave}
        />
      ) : null}
    </div>
  );
}
